import { Select } from "antd";
import { useMemo } from "react";
import styled from "styled-components";

import { DATE_TIME_PICKER_FORMAT, clockZoneAbbreviation } from "../../../helper/dateTime";
import { useGetAllTimeZones } from "../select/timezone";

const Abbrev = styled.span`
  align-items: center;
  background: var(--BLUE-LIGHT, #eef4ff);
  border-radius: 4px;
  color: var(--DARK-BLUE, #1249bf);
  display: inline-flex;
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.01em;
  line-height: 1;
  margin-inline-start: 2px;
  padding: 3px 6px;
  pointer-events: none;
  white-space: nowrap;
`;

const Footer = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 4px 0;
  width: 100%;
`;

const FooterLabel = styled.span`
  color: var(--GREY, #4a4a68);
  font-size: 12px;
  white-space: nowrap;
`;

/**
 * Compact timezone control for a clock DatePicker.
 *
 * Closed field shows IST/GMT as a highlighted chip in the suffix. The city
 * list lives in the calendar footer so it stays one Ant input.
 *
 * @param {{
 *   timeZone: string,
 *   onChange: function(string): void,
 *   disabled?: boolean,
 *   at?: string|number|Date|import("dayjs").Dayjs,
 *   placement?: "suffix"|"footer",
 * }} props
 * @return {JSX.Element}
 */
export const ClockZoneControl = ({
  timeZone,
  onChange,
  disabled = false,
  at,
  placement = "suffix",
}) => {
  const { data } = useGetAllTimeZones();
  const options = useMemo(
    () => (data || []).map(zone => ({ value: zone.id, label: zone.name })),
    [data],
  );
  const abbreviation = clockZoneAbbreviation(timeZone, at);

  if (placement === "suffix") {
    return (
      <Abbrev aria-label={`Timezone ${abbreviation}`} title={timeZone}>
        {abbreviation}
      </Abbrev>
    );
  }

  return (
    <Footer onMouseDown={event => event.preventDefault()}>
      <FooterLabel>Timezone</FooterLabel>
      <Select
        aria-label="Timezone"
        disabled={disabled}
        getPopupContainer={() => document.body}
        onChange={onChange}
        optionFilterProp="label"
        options={options}
        placeholder="Timezone"
        popupMatchSelectWidth={false}
        popupStyle={{ minWidth: 280, zIndex: 1100 }}
        showSearch
        size="small"
        style={{ flex: 1, minWidth: 0 }}
        value={timeZone}
      />
    </Footer>
  );
};

/**
 * Ant DatePicker extras that keep the zone inside the same input.
 *
 * @param {{
 *   timeZone: string,
 *   at?: string|number|Date|import("dayjs").Dayjs,
 *   onChange: function(string): void,
 *   disabled?: boolean,
 *   format?: string,
 * }} props
 * @return {{
 *   format: string,
 *   needConfirm: boolean,
 *   suffixIcon: JSX.Element,
 *   renderExtraFooter: function(): JSX.Element,
 * }}
 */
export const clockDatePickerProps = ({
  timeZone,
  at,
  onChange,
  disabled = false,
  format = DATE_TIME_PICKER_FORMAT,
}) => ({
  format,
  needConfirm: false,
  suffixIcon: <ClockZoneControl at={at} placement="suffix" timeZone={timeZone} />,
  renderExtraFooter: () => (
    <ClockZoneControl
      at={at}
      disabled={disabled}
      onChange={onChange}
      placement="footer"
      timeZone={timeZone}
    />
  ),
});
